import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  User, Shield, List, MessageCircle, Bell, Heart, Bookmark, Pencil, Smartphone, FileText, Headphones, Globe,
  ShieldCheck, LogOut, ChevronLeft, ChevronRight, Copy, Wallet, BadgeCheck, CalendarDays, KeyRound, History, ScrollText,
  Hash, MapPin, Building, Phone, Trash2,
} from "lucide-react";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";
import { useUnreadCount } from "@/lib/notifications";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api, imageUrl } from "@/lib/api";
import { findEmirate } from "@/lib/constants";

const POLL_MS = 8000;

function Row({ icon: Icon, label, onClick, badge, right, danger, testid }) {
  const { dir } = useLang();
  const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;
  return (
    <button onClick={onClick} data-testid={testid} className="w-full bg-white rounded-2xl border border-[#EFEBE0] px-4 py-4 flex items-center justify-between hover:bg-[#1A5C3A]/5 transition-colors">
      <div className="flex items-center gap-3">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${danger ? "bg-red-50" : "bg-[#E8EFEA]"}`}>
          <Icon className={`w-5 h-5 ${danger ? "text-red-600" : "text-[#1A5C3A]"}`} />
        </span>
        <span className={`font-medium text-[17px] ${danger ? "text-red-600" : "text-slate-800"}`}>{label}</span>
        {badge != null && <span className="bg-red-600 text-white text-[11px] font-bold rounded-full min-w-[22px] h-[22px] px-1.5 flex items-center justify-center" data-testid={`${testid}-badge`}>{badge}</span>}
      </div>
      <div className="flex items-center gap-2 text-slate-400">
        {right}
        <Chevron className="w-4 h-4" />
      </div>
    </button>
  );
}

function InfoLine({ icon: Icon, label, value, testid, ltr }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#EFEBE0] last:border-0">
      <span className="flex items-center gap-2 text-slate-500 text-[15px]"><Icon className="w-4 h-4 text-[#1A5C3A]" />{label}</span>
      <span className="font-semibold text-slate-800 text-[15px]" dir={ltr ? "ltr" : undefined} data-testid={testid}>{value || "—"}</span>
    </div>
  );
}

function ProfileCard({ user, L, lang, onEdit }) {
  const em = findEmirate(user?.emirate);
  const copyRef = async () => {
    try { await navigator.clipboard.writeText(user.wallet_ref); toast.success(L("تم نسخ الرقم المرجعي", "Reference number copied")); }
    catch { toast.error(L("تعذّر النسخ", "Copy failed")); }
  };
  return (
    <div className="bg-white rounded-3xl border border-[#EFEBE0] p-6 mb-6" data-testid="account-profile-card">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-16 h-16 rounded-full bg-[#DDEAE3] flex items-center justify-center overflow-hidden shrink-0">
          {user?.picture ? <img src={imageUrl(user.picture)} alt="" className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-[#1A5C3A]" />}
        </div>
        <div className="min-w-0">
          <h1 className="font-headline text-[22px] font-bold text-slate-900 truncate" data-testid="account-name">{user?.name}</h1>
          <p className="text-slate-500 text-[15px] truncate" data-testid="account-email">{user?.email}</p>
        </div>
      </div>
      <div className="flex items-center justify-between py-2.5 border-b border-[#EFEBE0]">
        <span className="flex items-center gap-2 text-slate-500 text-[15px]"><Hash className="w-4 h-4 text-[#1A5C3A]" />{L("الرقم المرجعي", "Reference no.")}</span>
        <button onClick={copyRef} disabled={!user?.wallet_ref} className="flex items-center gap-1.5 font-bold text-[#0F4D32] text-[15px]" data-testid="account-ref-copy">
          <span dir="ltr" data-testid="account-ref">{user?.wallet_ref || "—"}</span>{user?.wallet_ref && <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <InfoLine icon={MapPin} label={L("الإمارة", "Emirate")} value={em ? em[lang === "en" ? "en" : "ar"] : user?.emirate} testid="account-emirate" />
      <InfoLine icon={Building} label={L("المدينة / القرية", "City / Village")} value={user?.city} testid="account-city" />
      <InfoLine icon={Phone} label={L("رقم الهاتف", "Phone")} value={user?.phone} testid="account-phone" ltr />
      <InfoLine icon={MessageCircle} label={L("واتساب", "WhatsApp")} value={user?.whatsapp} testid="account-whatsapp" ltr />
      <button onClick={onEdit} className="mt-4 w-full h-11 rounded-xl bazaro-gradient text-white font-semibold flex items-center justify-center gap-2" data-testid="account-edit-profile">
        <Pencil className="w-4 h-4" />{L("تعديل الملف الشخصي", "Edit profile")}
      </button>
    </div>
  );
}

export default function Account() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const { t, toggleLang, lang } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const unread = useUnreadCount();
  const go = (p) => () => navigate(p);
  const [assigned, setAssigned] = useState(null);
  useEffect(() => { api.get("/support-chat/assigned").then(({ data }) => setAssigned(data)).catch(() => {}); }, []);
  const assignedUnread = (assigned || []).filter((c) => c.unread_agent > 0).length;

  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const { data } = await api.get("/account/summary");
        if (alive) setUser((prev) => (prev ? { ...prev, ...data.user, favorites: prev.favorites } : prev));
      } catch {}
    };
    pull();
    const id = setInterval(pull, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [setUser]);

  const doLogout = async () => { await logout(); navigate("/"); };
  const [delInfo, setDelInfo] = useState(null);
  const openDelete = async () => {
    try { const { data } = await api.get("/account/delete/check"); setDelInfo(data); }
    catch { toast.error(L("تعذّر التحقق، حاول مجدداً", "Could not verify, try again")); }
  };
  const balance = new Intl.NumberFormat("en-AE", { maximumFractionDigits: 2 }).format(user?.wallet_balance || 0);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 w-full py-8 flex-1">
        <ProfileCard user={user} L={L} lang={lang} onEdit={go("/edit-profile")} />
        <div className="space-y-3">
          {["admin", "moderator"].includes(user?.role) && <Row icon={Shield} label={L("لوحة الإدارة", "Admin panel")} onClick={go("/admin")} testid="account-admin-panel" />}
          <Row icon={Wallet} label={L("رصيد محفظتك", "Your wallet balance")} onClick={go("/wallet")} testid="account-wallet"
            right={<span className="text-[15px] font-bold text-[#1A5C3A]" dir="ltr" data-testid="account-wallet-balance">AED {balance}</span>} />
          <Row icon={List} label={L("إعلاناتي", "My ads")} onClick={go("/my-ads")} testid="account-my-ads" />
          <Row icon={CalendarDays} label={L("حجوزاتي", "My bookings")} onClick={go("/bookings")} testid="account-bookings" />
          <Row icon={BadgeCheck} label={L("سجل رخصتك التجارية", "Register your trade license")} onClick={go("/my-business")} testid="account-my-business" />
          <Row icon={CalendarDays} label={L("مواعيدي", "My appointments")} onClick={go("/appointments")} testid="account-appointments" />
          <Row icon={MessageCircle} label={L("الرسائل", "Messages")} onClick={go("/messages")} testid="account-messages" />
          <Row icon={Bell} label={L("الإشعارات", "Notifications")} badge={unread > 0 ? unread : null} onClick={go("/notifications")} testid="account-notifications" />
          <Row icon={Heart} label={L("المفضلة", "Favorites")} onClick={go("/favorites")} testid="account-favorites" />
          <Row icon={Bookmark} label={L("عمليات البحث المحفوظة", "Saved searches")} onClick={go("/saved-searches")} testid="account-saved-searches" />
          <Row icon={KeyRound} label={L("تغيير كلمة المرور", "Change password")} onClick={go("/change-password")} testid="account-change-password" />
          <Row icon={Smartphone} label={L("الأجهزة النشطة", "Active devices")} onClick={go("/active-devices")} testid="account-active-devices" />
          <Row icon={History} label={L("سجل مدفوعاتي", "My payment history")} onClick={go("/payment-history")} testid="account-payment-history" />
          <Row icon={FileText} label={L("إيصالات الدفع", "Payment receipts")} onClick={go("/payment-receipts")} testid="account-payment-receipts" />
          {user?.support_assigner && <Row icon={Headphones} label={L("توزيع محادثات الدعم", "Distribute support chats")} onClick={go("/support-assign")} testid="account-support-assign" />}
          {assigned?.length > 0 && <Row icon={Headphones} label={L("محادثات دعم مسندة إليّ", "Support chats assigned to me")} badge={assignedUnread || null} onClick={go("/assigned-support")} testid="account-assigned-support" />}
          <Row icon={Headphones} label={L("خدمة العملاء", "Customer service")} onClick={go("/customer-service")} testid="account-customer-service" />
          <Row icon={Globe} label={L("اللغة", "Language")} onClick={toggleLang} testid="account-language"
            right={<span className="text-[15px] font-semibold text-slate-600">{lang === "en" ? "English" : "العربية"}</span>} />
          <Row icon={ScrollText} label={L("الشروط والأحكام", "Terms & Conditions")} onClick={go("/terms")} testid="account-terms" />
          <Row icon={ShieldCheck} label={L("سياسة الخصوصية", "Privacy policy")} onClick={go("/privacy")} testid="account-privacy" />
          <Row icon={LogOut} label={L("تسجيل الخروج", "Sign Out")} onClick={doLogout} danger testid="account-logout" />
          {!["admin", "moderator"].includes(user?.role) && <Row icon={Trash2} label={L("حذف الحساب", "Delete account")} onClick={openDelete} danger testid="account-delete" />}
        </div>
        <DeleteAccountDialog open={!!delInfo} info={delInfo} onClose={() => setDelInfo(null)} L={L} onDeleted={async () => { setDelInfo(null); try { await logout(); } catch {} setUser(null); navigate("/"); }} />
        <p className="text-center text-[11px] text-slate-400 mt-8 leading-relaxed">{t("account_license")}</p>
      </main>
    </div>
  );
}
